// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * controls.ts — drag to aim, tap to rotate, buttons to drop (principle #19).
 *
 * Positioning is CONTINUOUS (no grid), so the natural control is a horizontal
 * drag: the brick follows the finger. A quick tap that does NOT move rotates.
 * Crucially, releasing a drag does NOT drop — dropping is an explicit button (or
 * Space) — so there are no accidental drops while lining a brick up.
 *
 * Pointer Events only, per-pointer, `setPointerCapture` on down, and
 * `pointercancel` handled like an aborted gesture. Position is read from
 * clientX − getBoundingClientRect (never offsetX, which scales oddly under DPR).
 */

const TAP_SLOP = 6;       // px of movement below which a release is a tap
const TAP_MS = 320;

export interface AimOpts {
  toWorldX: (clientX: number, rectLeft: number) => number;
  onAim: (worldX: number) => void;
  onTap: () => void;
  enabled: () => boolean;
}

export interface InputLayer { destroy(): void; }

export function attachAim(el: HTMLElement, opts: AimOpts): InputLayer {
  let pid: number | null = null;
  let startX = 0;
  let startY = 0;
  let startT = 0;
  let moved = false;

  const rectLeft = () => el.getBoundingClientRect().left;

  function down(e: PointerEvent): void {
    if (pid !== null || !opts.enabled()) return;
    pid = e.pointerId;
    startX = e.clientX;
    startY = e.clientY;
    startT = performance.now();
    moved = false;
    try { el.setPointerCapture(pid); } catch { /* capture is best-effort */ }
    e.preventDefault();
  }

  function move(e: PointerEvent): void {
    if (e.pointerId !== pid) return;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if (!moved && Math.hypot(dx, dy) > TAP_SLOP) moved = true;
    if (moved && opts.enabled()) opts.onAim(opts.toWorldX(e.clientX, rectLeft()));
    e.preventDefault();
  }

  function up(e: PointerEvent): void {
    if (e.pointerId !== pid) return;
    const dt = performance.now() - startT;
    if (!moved && dt < TAP_MS && opts.enabled()) opts.onTap();
    end();
    e.preventDefault();
  }

  function cancel(e: PointerEvent): void {
    if (e.pointerId !== pid) return;
    end();
  }

  function end(): void {
    if (pid !== null) { try { el.releasePointerCapture(pid); } catch { /* already gone */ } }
    pid = null;
    moved = false;
  }

  el.addEventListener('pointerdown', down);
  el.addEventListener('pointermove', move);
  el.addEventListener('pointerup', up);
  el.addEventListener('pointercancel', cancel);

  return {
    destroy() {
      end();
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', cancel);
    },
  };
}

export interface KeyOpts {
  onMove: (dir: number) => void;
  onRotate: (dir: number) => void;
  onDrop: () => void;
  onPause: () => void;
  onGust: () => void;
}

export function attachKeys(target: Window, opts: KeyOpts): InputLayer {
  function key(e: KeyboardEvent): void {
    switch (e.key) {
      case 'ArrowLeft': case 'a': case 'A': opts.onMove(-1); e.preventDefault(); break;
      case 'ArrowRight': case 'd': case 'D': opts.onMove(1); e.preventDefault(); break;
      case 'ArrowUp': case 'x': case 'X': opts.onRotate(1); e.preventDefault(); break;
      case 'z': case 'Z': opts.onRotate(-1); e.preventDefault(); break;
      case ' ': case 'ArrowDown': opts.onDrop(); e.preventDefault(); break;
      case 'p': case 'P': opts.onPause(); break;
      case 'g': case 'G': opts.onGust(); break;
      default: break;
    }
  }
  target.addEventListener('keydown', key);
  return { destroy() { target.removeEventListener('keydown', key); } };
}